/*
 * Throwaway preview — the seller-profile entry points, scoped to the
 * THREE real flows the operator described:
 *
 *   1. Discover suppliers / marketplace        — NOT LIVE in early days. Deferred.
 *   2. Existing supplier → direct PO           — No profile entry needed (user says).
 *   3. Seller invites buyer                    — THE flow we wire up. Two phases:
 *        a) Pre-accept   — buyer hasn't joined / connected yet
 *        b) Post-accept  — buyer is connected & operating in seller's storefront
 *
 * Plus seller-side surfaces for managing the profile.
 *
 * Hit /preview/seller-profile-entrypoints to view.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail, ArrowRight, ExternalLink, Eye, Copy, Settings, Store,
  Building2, ShieldCheck, ChevronDown, Check, Home, Edit3, Sparkles,
  ChevronRight, Star, MapPin, MessageSquare, Info, Lock, Truck,
} from "lucide-react";

const TABS = [
  { key: "pre",    label: "1 · Pre-accept",  sub: "Invite arrives" },
  { key: "post",   label: "2 · Post-accept", sub: "Buyer connected" },
  { key: "seller", label: "3 · Seller side", sub: "Manages profile" },
];

export default function SellerProfileEntryPointsPreview() {
  const [tab, setTab] = useState("pre");
  return (
    <div className="min-h-screen bg-secondary/30">
      {/* sticky header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-[1240px] mx-auto px-8 py-4 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/50">Preview · internal</div>
            <div className="display text-lg mt-0.5">Where the seller profile lives — scoped to your real flows</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-full border border-border bg-white p-1">
              {TABS.map(t => (
                <button key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`rounded-full px-4 h-9 text-left transition-colors ${tab === t.key ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                  <div className="text-[11.5px] mono uppercase tracking-[0.12em]">{t.label}</div>
                  <div className={`text-[10px] mt-[-2px] ${tab === t.key ? "text-background/60" : "text-foreground/45"}`}>{t.sub}</div>
                </button>
              ))}
            </div>
            <Link to="/preview/seller-profile" className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/50 hover:text-foreground">→ The profile page</Link>
            <Link to="/" className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/50 hover:text-foreground ml-1">← Back</Link>
          </div>
        </div>
      </div>

      <div className="max-w-[1240px] mx-auto px-8 py-10">
        {/* The flow map at the top — same on every tab */}
        <FlowMap active={tab}/>

        {tab === "pre"    && <PreAcceptEntries/>}
        {tab === "post"   && <PostAcceptEntries/>}
        {tab === "seller" && <SellerSideEntries/>}

        <ScopingFooter/>
      </div>
    </div>
  );
}

/* ── Flow-map header — visualises the 3 buyer-seller flows ────────── */
function FlowMap({ active }) {
  const cells = [
    { key: "marketplace", n: "①", title: "Discover suppliers", sub: "Marketplace · NOT LIVE yet", state: "deferred" },
    { key: "po",          n: "②", title: "Existing supplier → PO", sub: "Buyer already knows seller · No profile needed", state: "skip" },
    { key: "invite",      n: "③", title: "Seller invites buyer", sub: "← THIS is what we're wiring", state: "active" },
  ];
  return (
    <div className="grid sm:grid-cols-3 gap-3 mb-10">
      {cells.map(c => {
        const isActive = (active === "pre" || active === "post" || active === "seller") && c.state === "active";
        const cls = c.state === "deferred"
          ? "border-border bg-white/40 opacity-55"
          : c.state === "skip"
          ? "border-border bg-white/60"
          : isActive ? "border-foreground bg-foreground text-background" : "border-foreground/30 bg-white";
        return (
          <div key={c.key} className={`rounded-2xl border p-4 ${cls}`}>
            <div className="flex items-center gap-2">
              <div className={`display text-2xl ${isActive ? "text-accent" : c.state === "deferred" ? "text-foreground/35" : "text-foreground/40"}`}>{c.n}</div>
              <div className="flex-1">
                <div className="display text-base">{c.title}</div>
                <div className={`text-[11.5px] mt-0.5 ${isActive ? "text-background/65" : "text-foreground/55"}`}>{c.sub}</div>
              </div>
              {c.state === "deferred" && <Lock className="w-3.5 h-3.5 text-foreground/35"/>}
              {c.state === "skip"     && <Check className="w-3.5 h-3.5 text-foreground/35"/>}
              {isActive               && <Sparkles className="w-3.5 h-3.5 text-accent"/>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══ TAB 1 — PRE-ACCEPT ════════════════════════════════════════════ */
function PreAcceptEntries() {
  return (
    <div className="space-y-10">
      <Header
        eyebrow="Phase 1 · Buyer hasn't accepted yet"
        title="2 entry points — both before the buyer joins"
        sub="The buyer needs to vet the seller before clicking Accept. Both surfaces are reachable WITHOUT a TRAQO login."
      />

      {/* Invite email */}
      <Surface
        n="01"
        path="Invite email · sent by your transactional mailer"
        icon={Mail}
        title="The invite email itself"
        desc="When the seller hits 'Send invite', the email body includes a 'View seller profile' link directly above the Accept CTA."
        callouts={[
          "Link uses the public /s/:handle URL — no login required to read.",
          "Lets cautious buyers do due-diligence WITHOUT clicking the Accept-style link, which feels committal.",
          "Email open-rate × profile-view rate = your earliest acquisition funnel signal.",
        ]}
      >
        <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden max-w-[560px]">
          <div className="px-5 py-3 border-b border-border bg-secondary/50 text-[11.5px] text-foreground/65 flex items-center gap-2">
            <Mail className="w-3.5 h-3.5"/> From: Glow Co. via TRAQO &lt;hello@traqo.com&gt;
          </div>
          <div className="p-6 text-[14px] leading-relaxed text-foreground/85">
            <div className="display text-xl text-foreground">You're invited — Glow Co. wants you on their wholesale.</div>
            <p className="mt-3 text-foreground/70">Hi Sarah —</p>
            <p className="mt-2 text-foreground/70">
              We'd love to onboard your shop on our wholesale program. Net-30 terms, $25,000 credit top-up,
              and same-day shipping when ordered before noon PT.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <button className="rounded-full bg-foreground text-background px-5 h-10 text-[13px] font-medium">Accept invitation</button>
              <Annotated>
                <a href="#" className="text-[13px] underline underline-offset-2 text-foreground/75 hover:text-foreground">View Glow Co.'s profile first →</a>
              </Annotated>
            </div>
            <p className="mt-6 text-[11.5px] text-foreground/45">Powered by TRAQO · The wholesale OS</p>
          </div>
        </div>
      </Surface>

      {/* AcceptInvite landing page */}
      <Surface
        n="02"
        path="/invites/:token · the AcceptInvite landing page"
        icon={ShieldCheck}
        title="The accept-invite page"
        desc="Add a 'View seller profile' secondary link beside the Accept button. Opens /s/:handle in a new tab so the buyer can vet without losing their place on the invite page."
        callouts={[
          "Lives next to the existing 'Accept invitation' button — buyer's last chance to vet before committing.",
          "Opens in a new tab so the invite token / page state is preserved if they bounce around.",
          "Helps the AcceptInvite page do double-duty: pitch + verify.",
        ]}
      >
        <div className="rounded-2xl border border-border overflow-hidden max-w-[760px] grid grid-cols-2">
          {/* Left — co-branded panel (recreated mini) */}
          <div className="p-6 bg-[#0a1f1c] text-white">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded grid place-items-center font-bold display bg-[#e8ff3a] text-[#0a1f1c]">G</span>
              <span className="display font-semibold text-sm">Glow Co.</span>
            </div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-[#e8ff3a] mt-12">You're invited</div>
            <div className="display text-2xl mt-2 leading-tight">Glow Co. wants you<br/>on their wholesale.</div>
            <div className="mt-5 space-y-1.5 text-[12.5px] text-white/85">
              <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#e8ff3a]"/>Net-30 terms</div>
              <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#e8ff3a]"/>+$25,000 credit top-up</div>
              <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#e8ff3a]"/>Private linesheet</div>
            </div>
          </div>
          {/* Right — action panel */}
          <div className="p-6 bg-white">
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55">Invitation details</div>
            <div className="display text-xl tracking-tight mt-1.5">Accept &amp; start buying</div>
            <p className="text-foreground/65 text-[12.5px] mt-2 leading-relaxed">
              You'll be connected to Glow Co. and land on their storefront. Your credit line stays unified across every supplier on TRAQO.
            </p>
            <div className="mt-4 rounded-lg border border-foreground/10 bg-secondary/40 p-3 text-[11.5px] space-y-1.5">
              <div className="flex justify-between"><span className="text-foreground/55">Invited email</span><span className="font-medium">sarah@boutique.co</span></div>
              <div className="flex justify-between"><span className="text-foreground/55">Default terms</span><span className="font-medium">Net-30</span></div>
            </div>
            <div className="mt-5 flex items-center gap-2.5 flex-wrap">
              <button className="rounded-full px-4 h-10 bg-[#0a1f1c] text-[#e8ff3a] text-[13px] font-medium inline-flex items-center gap-1.5">
                Accept invitation <ArrowRight className="w-3.5 h-3.5"/>
              </button>
              <Annotated>
                <a href="#" className="rounded-full border border-foreground/20 hover:border-foreground bg-white px-4 h-10 inline-flex items-center gap-1.5 text-[12.5px]">
                  <Eye className="w-3.5 h-3.5"/> View seller profile
                </a>
              </Annotated>
            </div>
          </div>
        </div>
      </Surface>
    </div>
  );
}

/* ═══ TAB 2 — POST-ACCEPT ═══════════════════════════════════════════ */
function PostAcceptEntries() {
  return (
    <div className="space-y-10">
      <Header
        eyebrow="Phase 2 · Buyer accepted, now connected"
        title="3 entry points — inside the buyer portal"
        sub="Once connected, the buyer is operating in the seller's storefront. They need to be able to refresh on policies + terms anytime — without leaving the seller's context."
      />

      {/* SupplierSwitcher dropdown */}
      <Surface
        n="01"
        path="Top-right SupplierSwitcher dropdown · every buyer page"
        icon={ChevronDown}
        title="Supplier switcher dropdown row"
        desc="The dropdown that lists all connected suppliers (always-on chrome). Each row gets a 'View profile' link that opens /s/:handle in a new tab."
        callouts={[
          "Always-on access — works whether buyer is in storefront-mode or hub-mode.",
          "Opens in a NEW TAB so the buyer doesn't lose the page they were on (e.g. mid-PO).",
          "Single highest-frequency surface for a connected buyer — sees the switcher constantly.",
        ]}
      >
        <div className="relative">
          {/* mock top bar */}
          <div className="rounded-2xl border border-border bg-white p-3 flex items-center gap-3 max-w-[760px]">
            <Home className="w-4 h-4 text-foreground/55"/>
            <span className="text-[12px] text-foreground/65">Buyer hub · Top bar</span>
            <div className="ml-auto inline-flex items-center gap-2 px-3 h-9 rounded-full border border-foreground/15 bg-white text-[13px]" style={{ borderColor: "#e8ff3a80" }}>
              <span className="w-5 h-5 rounded grid place-items-center text-[10px] font-bold display bg-[#0a1f1c] text-[#e8ff3a]">G</span>
              <span>Glow Co. Beauty</span>
              <ChevronDown className="w-3.5 h-3.5 text-foreground/40"/>
            </div>
          </div>
          {/* mock dropdown */}
          <div className="absolute right-0 mt-2 w-[340px] rounded-2xl border border-foreground/10 bg-white shadow-xl overflow-hidden">
            <div className="p-2 border-b border-border">
              <div className="px-3 py-2.5 flex items-center gap-3 rounded-lg hover:bg-secondary/50">
                <span className="w-8 h-8 rounded-lg bg-foreground grid place-items-center text-background"><Home className="w-3.5 h-3.5"/></span>
                <div className="flex-1"><div className="text-[13px]">TRAQO Hub · All suppliers</div></div>
              </div>
            </div>
            <div className="p-2">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/45 px-3 py-1.5">Connected · 1</div>
              <div className="px-3 py-2.5 flex items-center gap-3 rounded-lg bg-secondary/40">
                <span className="w-8 h-8 rounded-lg grid place-items-center font-bold display bg-[#0a1f1c] text-[#e8ff3a]">G</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">Glow Co. Beauty</div>
                  <Annotated>
                    <Link to="/preview/seller-profile" className="text-[11.5px] text-foreground/60 hover:text-foreground inline-flex items-center gap-1">
                      <Eye className="w-3 h-3"/> View profile
                    </Link>
                  </Annotated>
                </div>
                <Check className="w-3.5 h-3.5 text-emerald-600"/>
              </div>
            </div>
          </div>
          <div className="h-44"/>{/* spacer for absolute dropdown */}
        </div>
      </Surface>

      {/* Storefront mode top header */}
      <Surface
        n="02"
        path="Storefront-mode header · whenever the buyer is scoped to a seller"
        icon={Building2}
        title="Click the seller's name in storefront-mode"
        desc="When the buyer is scoped to a seller (storefront mode), the seller's name + logo appear at the top of every page. We make the name clickable + add a tiny info-icon."
        callouts={[
          "Subtle, contextual — only visible when storefront-mode is active.",
          "Info-icon (ⓘ) is a universal 'see metadata' affordance — buyers expect it to do something.",
          "Both name and icon link to /s/:handle (in a new tab).",
        ]}
      >
        <div className="rounded-2xl border border-border bg-white p-4 max-w-[760px]" style={{ borderColor: "#e8ff3a40" }}>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg grid place-items-center font-bold display bg-[#0a1f1c] text-[#e8ff3a]">G</span>
            <div>
              <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">Storefront · live</div>
              <Annotated>
                <Link to="/preview/seller-profile" className="display text-lg inline-flex items-center gap-1.5 hover:text-foreground/70 group">
                  Glow Co. Beauty
                  <Info className="w-3.5 h-3.5 text-foreground/45 group-hover:text-foreground"/>
                </Link>
              </Annotated>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="chip rounded-full bg-secondary text-foreground/65 px-2.5 h-7 inline-flex items-center text-[11px]">$25k credit · Net-30</span>
            </div>
          </div>
        </div>
      </Surface>

      {/* /buyer/suppliers connected card */}
      <Surface
        n="03"
        path="/buyer/suppliers · 'My suppliers' canonical list"
        icon={Store}
        title="My-suppliers card"
        desc="Your existing canonical 'My suppliers' page. Each connected-supplier card gets a 'View profile' button alongside the existing CTAs."
        callouts={[
          "Buyers come here specifically to manage seller relationships → highest intent surface.",
          "Sits next to existing buttons (Open storefront / Send PO) — equal weight to relationship-management.",
          "Button uses Eye icon to signal a read-only public page (vs. interactive controls beside it).",
        ]}
      >
        <div className="rounded-2xl border border-border bg-white p-5 max-w-[560px]">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl grid place-items-center display text-xl bg-[#0a1f1c] text-[#e8ff3a]">G</div>
            <div className="flex-1">
              <div className="font-medium">Glow Co. Beauty</div>
              <div className="text-[12px] text-foreground/55 mt-0.5 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3"/> Los Angeles, CA · Connected Apr 2025
              </div>
            </div>
            <span className="chip rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 h-6 inline-flex items-center text-[11px]">Connected</span>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
            <Mini l="Orders" v="14"/>
            <Mini l="Spend YTD" v="$48.2K"/>
            <Mini l="On-time" v="100%"/>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button className="rounded-full bg-foreground text-background px-3.5 h-9 inline-flex items-center gap-1.5 text-[12.5px]">
              <Store className="w-3.5 h-3.5"/> Open storefront
            </button>
            <Annotated>
              <Link to="/preview/seller-profile" className="rounded-full border border-foreground/20 hover:border-foreground bg-white px-3.5 h-9 inline-flex items-center gap-1.5 text-[12.5px]">
                <Eye className="w-3.5 h-3.5"/> View profile
              </Link>
            </Annotated>
            <button className="rounded-full border border-foreground/20 hover:border-foreground bg-white px-3.5 h-9 inline-flex items-center gap-1.5 text-[12.5px]">
              <MessageSquare className="w-3.5 h-3.5"/> Message
            </button>
          </div>
        </div>
      </Surface>
    </div>
  );
}

/* ═══ TAB 3 — SELLER MANAGES THEIR OWN ═══════════════════════════════ */
function SellerSideEntries() {
  return (
    <div className="space-y-10">
      <Header
        eyebrow="Phase 3 · Seller-side controls"
        title="3 entry points — for sellers to manage their public profile"
        sub="The seller is the one who fills in the profile in the first place — and shares the URL on emails, decks, business cards. Three places to support that."
      />

      {/* Settings panel */}
      <Surface
        n="01"
        path="/seller/settings → new 'Public profile' panel"
        icon={Settings}
        title="Settings → Public profile"
        desc="A new panel inside Settings that sits next to Operations (where the QI window already lives). Profile READS from those operations values for the public-facing display."
        callouts={[
          "QI window / lead time stay in Operations — single source of truth, no duplication.",
          "'Preview public page' opens /s/:handle (seller's-own viewer state) so the seller sees what buyers see.",
          "'Copy shareable link' = the viral lever — sellers paste the URL into cold emails / pitch decks.",
        ]}
      >
        <div className="rounded-2xl border border-border bg-white p-5 max-w-[640px]">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-foreground/65"/>
            <div className="display text-lg">Public profile</div>
            <Annotated>
              <span className="chip rounded-full bg-accent/20 border border-accent/40 px-2 h-6 inline-flex items-center text-[11px] mono uppercase tracking-[0.12em] ml-2">New</span>
            </Annotated>
          </div>
          <div className="text-[13px] text-foreground/65 mt-1 max-w-md leading-relaxed">
            Anything you set here shows up at <code className="text-foreground/85">traqo.com/s/glow-co</code>. Buyers see it when you invite them.
          </div>
          <div className="mt-5 space-y-3">
            <Field l="Public handle" v="glow-co" hint="Used in your shareable URL."/>
            <Field l="Tagline"        v="Clean, cruelty-free skincare. Made in California."/>
            <Field l="About blurb"    v="Glow Co. is a Los Angeles–based wholesale beauty supplier…" multiline/>
            <Field l="Categories"     v="Beauty · Skincare · Made in USA · Cruelty-free"/>
          </div>
          <div className="mt-5 pt-4 border-t border-border flex items-center gap-2 flex-wrap">
            <Annotated>
              <Link to="/preview/seller-profile" className="rounded-full border border-foreground/20 hover:border-foreground bg-white px-4 h-10 inline-flex items-center gap-1.5 text-[12.5px]">
                <Eye className="w-4 h-4"/> Preview public page
              </Link>
            </Annotated>
            <Annotated>
              <button className="rounded-full border border-foreground/20 hover:border-foreground bg-white px-4 h-10 inline-flex items-center gap-1.5 text-[12.5px]">
                <Copy className="w-4 h-4"/> Copy shareable link
              </button>
            </Annotated>
            <button className="ml-auto rounded-full bg-foreground text-background px-4 h-10 text-[12.5px]">Save</button>
          </div>
        </div>
      </Surface>

      {/* Sidebar shortcut */}
      <Surface
        n="02"
        path="Seller portal sidebar · always-on shortcut"
        icon={Store}
        title="'My profile' in the Account section"
        desc="One-click access from anywhere. Clicking opens the public page so the seller sees what buyers see (the seller-viewer state of /s/:handle)."
        callouts={[
          "Always-on shortcut — sellers don't have to hunt through Settings panels.",
          "Lives under 'Account' alongside Settings: this is identity / public-facing config.",
          "Seller-viewer state shows the 'Edit profile' inline button at the top.",
        ]}
      >
        <div className="rounded-2xl border border-border bg-white p-3 w-[260px]">
          <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/45 px-3 mt-1 mb-1.5">Account</div>
          <SidebarRow Icon={Settings} label="Settings"/>
          <Annotated>
            <SidebarRow Icon={Store} label="My public profile" badge="New" highlight/>
          </Annotated>
          <SidebarRow Icon={Star} label="Help & Support"/>
        </div>
      </Surface>

      {/* Invite-flow hint */}
      <Surface
        n="03"
        path="When seller hits 'Send invite' · invite-flow modal"
        icon={Mail}
        title="Profile-completeness nudge in the invite flow"
        desc="When a seller is about to send an invite, if their profile is incomplete, show a friendly nudge — buyers vet sellers via the profile, so a barebones one will hurt acceptance rates."
        callouts={[
          "Critical UX: invite acceptance rates drop sharply when profiles are empty.",
          "Non-blocking — seller can send anyway. But the nudge surfaces the cost.",
          "One-click 'Complete profile first' deep-links to /seller/settings → Public profile panel.",
        ]}
      >
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 max-w-[640px]">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-200 grid place-items-center shrink-0">
              <Edit3 className="w-4 h-4 text-amber-900"/>
            </div>
            <div className="flex-1">
              <div className="display text-base text-amber-900">Your public profile is barely filled in</div>
              <div className="text-[12.5px] text-amber-900/80 mt-0.5 leading-relaxed">
                Buyers vet sellers before accepting invites. Sellers with a complete profile see ~2× higher accept rates. Add an About blurb + categories before sending.
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Annotated>
                  <Link to="/preview/seller-profile" className="rounded-full bg-amber-900 text-amber-50 px-3.5 h-9 inline-flex items-center gap-1.5 text-[12.5px] font-medium">
                    Complete profile first <ArrowRight className="w-3.5 h-3.5"/>
                  </Link>
                </Annotated>
                <button className="text-[11.5px] text-amber-900/65 hover:text-amber-900 px-2">Send anyway</button>
              </div>
            </div>
          </div>
        </div>
      </Surface>
    </div>
  );
}

/* ── Footer · scoping note ──────────────────────────────────────────── */
function ScopingFooter() {
  return (
    <div className="mt-12 pt-6 border-t border-border space-y-3 text-[12px] text-foreground/55 leading-relaxed max-w-3xl">
      <div className="flex items-start gap-2">
        <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
        <div>
          <strong className="text-foreground/75">Deferred to v2 (when Discover Suppliers launches):</strong> seller-name links inside
          <code className="mx-1">/buyer/catalog</code>product cards, marketplace cards, and the off-platform "discovered suppliers" rows. We don't expose these in early days — too few sellers / buyers to power public discovery.
        </div>
      </div>
      <div className="flex items-start gap-2">
        <Check className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
        <div>
          <strong className="text-foreground/75">Skipped (per your guidance):</strong> existing-supplier direct-PO flow at
          <code className="mx-1">/buyer/po-inbox</code>. Buyers already know these sellers offline — no profile entry needed.
        </div>
      </div>
      <div className="flex items-start gap-2">
        <Truck className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
        <div>
          <strong className="text-foreground/75">Note on dropship:</strong> the dropship MSA card lives at
          <code className="mx-1">/buyer/dropship</code> only when the dropship app is installed. Adding the seller-name link there is a small bonus that piggybacks on this work — happy to include or skip per your call.
        </div>
      </div>
    </div>
  );
}

/* ═══ atoms ═════════════════════════════════════════════════════════ */
function Header({ eyebrow, title, sub }) {
  return (
    <div>
      <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/50">{eyebrow}</div>
      <h1 className="display text-3xl tracking-tight mt-1.5">{title}</h1>
      <p className="text-[13.5px] text-foreground/65 mt-2 max-w-2xl leading-relaxed">{sub}</p>
    </div>
  );
}

function Surface({ n, path, icon: Icon, title, desc, callouts, children }) {
  return (
    <section className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
      <div>
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <div className="display text-2xl text-foreground/30">{n}</div>
          <span className="chip rounded-full border border-border bg-white px-2.5 h-6 inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-[0.12em] text-foreground/65">
            <Icon className="w-3 h-3"/> {path}
          </span>
        </div>
        <div className="display text-xl">{title}</div>
        <p className="text-[13px] text-foreground/65 mt-1.5 max-w-xl leading-relaxed">{desc}</p>
        <div className="mt-5">{children}</div>
      </div>
      <Callouts items={callouts}/>
    </section>
  );
}

function Callouts({ items }) {
  return (
    <aside className="rounded-2xl border border-border bg-white/60 p-5">
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-3">Why placed here</div>
      <ul className="space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="text-[12.5px] text-foreground/75 leading-relaxed flex gap-2">
            <span className="mono text-foreground/40 shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function Annotated({ children }) {
  return (
    <span className="relative inline-flex">
      <span className="absolute -inset-1.5 rounded-2xl ring-2 ring-accent/80 ring-offset-1 ring-offset-white pointer-events-none" aria-hidden/>
      {children}
    </span>
  );
}

function Mini({ l, v }) {
  return (
    <div>
      <div className="display text-base">{v}</div>
      <div className="text-[10.5px] mono uppercase tracking-[0.12em] text-foreground/50 mt-0.5">{l}</div>
    </div>
  );
}

function Field({ l, v, hint, multiline }) {
  return (
    <div>
      <div className="text-[12px] mono uppercase tracking-[0.14em] text-foreground/55">{l}</div>
      {multiline ? (
        <div className="mt-1 rounded-lg border border-border bg-secondary/40 px-3 py-2.5 text-[13px] text-foreground/80 leading-relaxed">{v}</div>
      ) : (
        <div className="mt-1 rounded-lg border border-border bg-secondary/40 h-9 px-3 inline-flex items-center text-[13px] text-foreground/80 w-full max-w-md">{v}</div>
      )}
      {hint && <div className="text-[11.5px] text-foreground/50 mt-1">{hint}</div>}
    </div>
  );
}

function SidebarRow({ Icon, label, badge, highlight }) {
  return (
    <div className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] ${highlight ? "bg-foreground/5" : ""}`}>
      <Icon className={`w-3.5 h-3.5 ${highlight ? "text-foreground" : "text-foreground/55"}`}/>
      <span className={highlight ? "text-foreground" : "text-foreground/75"}>{label}</span>
      {badge && <span className="chip rounded-full bg-accent/30 border border-accent/40 px-2 h-5 inline-flex items-center text-[10px] mono uppercase tracking-[0.12em] ml-auto">{badge}</span>}
      <ChevronRight className={`w-3.5 h-3.5 ${highlight ? "text-foreground/65" : "text-foreground/30"} ${badge ? "" : "ml-auto"}`}/>
    </div>
  );
}
